import { useEffect, useRef, useState } from "react";
import { motion, useInView, animate } from "framer-motion";
import Marquee from "react-fast-marquee";
import { cn } from "@/lib/utils";

export const EASE = [0.22, 1, 0.36, 1];

export function Reveal({ children, delay = 0, y = 32, className }) {
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.8, delay, ease: EASE }}
    >
      {children}
    </motion.div>
  );
}

export function MaskedLines({ lines, className, lineClassName, delay = 0 }) {
  return (
    <h1 className={className}>
      {lines.map((line, i) => {
        const text = typeof line === "string" ? line : line.t;
        const extra = typeof line === "string" ? "" : line.c;
        return (
          <span key={i} className="block overflow-hidden pb-[0.1em] -mb-[0.1em]">
            <motion.span
              className={cn("block will-change-transform", lineClassName, extra)}
            initial={{ y: "115%" }}
            animate={{ y: "0%" }}
            transition={{ duration: 1, delay: delay + i * 0.14, ease: EASE }}
          >
            {text}
          </motion.span>
        </span>
        );
      })}
    </h1>
  );
}

export function Chapter({ index, label, dark = false, className }) {
  return (
    <Reveal y={16} className={cn("flex items-center gap-4", className)}>
      <span className="font-heading text-sm font-bold text-gold">{index}</span>
      <span className="h-px w-12 bg-gold" />
      <span className={cn("text-xs font-semibold uppercase tracking-[0.35em]", dark ? "text-white/70" : "text-navy/60")}>
        {label}
      </span>
    </Reveal>
  );
}

export function Counter({ to, suffix = "", className }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, to, { duration: 1.8, ease: "easeOut", onUpdate: (v) => setVal(Math.round(v)) });
    return () => controls.stop();
  }, [inView, to]);
  return (
    <span ref={ref} className={className}>
      {val}
      {suffix}
    </span>
  );
}

export function MarqueeStrip({ items, dark = false }) {
  return (
    <div
      className={cn("border-y py-5", dark ? "border-white/10 bg-navy" : "border-navy/10 bg-gold")}
      data-testid="editorial-marquee"
    >
      <Marquee speed={35} gradient={false} pauseOnHover>
        {items.map((item) => (
          <span key={item} className="mx-8 flex items-center gap-8">
            <span
              className={cn(
                "font-heading text-sm font-bold uppercase tracking-[0.4em]",
                dark ? "text-white/60" : "text-navy"
              )}
            >
              {item}
            </span>
            <span className={cn("block h-2 w-2 rotate-45", dark ? "bg-gold" : "bg-navy")} />
          </span>
        ))}
      </Marquee>
    </div>
  );
}
